import PoiTypeDrawer, { type PoiTypeDraft } from '@components/Customization/PoiTypes/PoiTypeDrawer';
import PoiTypeRow from '@components/Customization/PoiTypes/PoiTypeRow';
import Header from '@components/Shared/Layout/Header';
import SearchInput from '@components/Shared/SearchInput';
import { ROUTE_PATHS } from '@constants/routes';
import { dropDraftsNotListed, useUnsavedDrafts } from '@lib/customization/unsavedWork';
import { useLocalization } from '@lib/localization';
import { Box, Button, Divider, LinearProgress, Paper, Typography } from '@mui/material';
import { useAddPoiTypeMutation, useGetPoiTypesQuery, type PoiType } from '@store/api/poiTypeEndpoints';
import { useGetTeamDirectoryQuery } from '@store/api/teamEndpoints';
import { usePermission } from '@store/hooks';
import { type RootState } from '@store/store';
import { useEffect, useMemo, useState } from 'react';
import { PlusIcon } from 'react-line-awesome';
import { useSelector } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';

/**
 * The catalogue: a list and a way to add to it. The languages its labels are written in are
 * the server's, set on the server configuration page.
 */
const CustomPoiTypesPage = () => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const { can, userId } = usePermission();
  const sessionTeams = useSelector((state: RootState) => state.auth.user?.teams ?? []);

  const { data, isError, fulfilledTimeStamp, refetch } = useGetPoiTypesQuery();
  const poiTypes = useMemo(() => data ?? [], [data]);

  // A catalogue that could not be read is not an empty one: saying "no POI types yet" would
  // send an author looking for types that are still there. A re-read that fails leaves what
  // was read on screen.
  const isLoadFailed = isError && !data;

  // The types the reader left unsaved changes to in the designer. The drafts are kept in the
  // tab's memory only, so the list is where their author sees that they are still there.
  const unsaved = useUnsavedDrafts('poi-type', userId);

  // A draft of a type the catalogue no longer holds has no row to carry that mark and no
  // designer left to discard it in: somebody else deleted the type, its team is gone, or the
  // reader was taken out of it. It is given up by a catalogue that was answered after the
  // draft was last kept (`dropDraftsNotListed`). When it was answered is enough here, as it
  // is not for a list of things that are opened by a read of their own: a type is opened
  // from the catalogue's own row, so its draft was kept under a catalogue that held the
  // type, and one that came after and does not hold it was read once the type had gone.
  useEffect(() => {
    if (!data || fulfilledTimeStamp === undefined) return;

    dropDraftsNotListed('poi-type', userId, data.map(({ _id }) => _id), fulfilledTimeStamp);
  }, [userId, data, fulfilledTimeStamp]);

  const [addPoiType, { isLoading: isCreating }] = useAddPoiTypeMutation();

  const [search, setSearch] = useState('');
  const [newTypeOpen, setNewTypeOpen] = useState(false);

  /**
   * Teams a type may be created in.
   *
   * A global author — sysadmin, admin — may create one in any team, and typically belongs
   * to none, so their own memberships cannot produce the list: filtering those left the
   * list empty and hid the button from the people most able to use it. They get the whole
   * directory; everyone else gets the teams their own role grants it in.
   */
  const canCreateAnywhere = can('poi_type:post');
  const { data: teamDirectory = [] } = useGetTeamDirectoryQuery(undefined, { skip: !canCreateAnywhere });

  const ownableTeams = useMemo(
    () => (canCreateAnywhere
      ? teamDirectory.map((team) => ({ id: team._id, name: team.name }))
      : sessionTeams
        .filter((team) => can('poi_type:post', team.id))
        .map((team) => ({ id: team.id, name: team.name }))),
    [canCreateAnywhere, teamDirectory, sessionTeams, can]
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return poiTypes;

    return poiTypes.filter((poiType) =>
      Object.values(poiType.name).some((value) => value?.toLowerCase().includes(query))
      || poiType.description?.toLowerCase().includes(query));
  }, [poiTypes, search]);

  const openType = (poiType: PoiType) =>
    navigate(generatePath(ROUTE_PATHS.CUSTOMIZATIONS_POI_TYPE, { poiTypeId: poiType._id }));

  const handleCreate = async (draft: PoiTypeDraft) => {
    const result = await addPoiType({
      teamId: draft.teamId,
      name: draft.name,
      description: draft.description,
      inheritFrom: draft.inheritFrom,
      fields: [],
      groups: [],
    });

    if (result.error) return;

    setNewTypeOpen(false);

    // Straight to the new type's page: it has no fields yet, and that is the next thing
    // the author is here to do.
    if ('data' in result && result.data) {
      navigate(generatePath(ROUTE_PATHS.CUSTOMIZATIONS_POI_TYPE, { poiTypeId: result.data.id }));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Header />

      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            marginBottom: '12px'
          }}
        >
          <SearchInput
            text={search}
            placeholder={t('Customization_SearchPoiTypes')}
            action={setSearch}
            count={filtered.length}
          />
          {ownableTeams.length > 0 && (
            <Button
              variant="contained"
              startIcon={<PlusIcon />}
              onClick={() => setNewTypeOpen(true)}
              // Not before the catalogue has been read: a new type can extend one in it, and
              // a catalogue that failed to load would offer no parent.
              disabled={!data}
            >
              {t('Customization_NewPoiType')}
            </Button>
          )}
        </Box>

        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          {/* No catalogue yet and no failure: it is on its way, a retry after a failure included. */}
          {!data && !isLoadFailed && <LinearProgress />}

          {isLoadFailed && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('Customization_PoiTypesLoadFailed')}</Typography>
              <Button size="small" variant="outlined" color="inherit" sx={{ mt: 1.5 }} onClick={() => { void refetch(); }}>
                {t('ReportPreview_Retry')}
              </Button>
            </Box>
          )}

          {data && filtered.length === 0 && (
            <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              {t('Customization_NoPoiTypes')}
            </Typography>
          )}

          {filtered.map((poiType, position) => (
            <Box key={poiType._id}>
              {position > 0 && <Divider />}
              <PoiTypeRow poiType={poiType} unsaved={unsaved.has(poiType._id)} onOpen={() => openType(poiType)} />
            </Box>
          ))}
        </Paper>
      </Box>

      {/* Parent candidates go unfiltered here on purpose: a type that does not exist yet has
          no descendants, so every type the reader can see is one it may legally extend. The
          detail page is where that set has to be narrowed. */}
      <PoiTypeDrawer
        open={newTypeOpen}
        poiType={null}
        parentCandidates={poiTypes}
        teams={ownableTeams}
        saving={isCreating}
        onClose={() => setNewTypeOpen(false)}
        onSave={handleCreate}
      />
    </Box>
  );
};

export default CustomPoiTypesPage;
